import React, { Fragment, useContext, useState } from "react";

import ProjectBasics from "./ProjectBasics";
import ClientBasics from "./ClientBasics";
import ProjectManagers from "./ProjectManagers";
import ProjectTags from "./ProjectTags";
import ContractTypes from "./ContractTypes";
import MultipleAddressesForm from "./MultipleAddressesForm";
import MultipleActivitiesForm from "./MultipleActivitiesForm";
import { DEFAULT_PROJECT_STATE } from "./constants";
import { formatSourceText } from "./helpers";
import { canCreateProject } from "./validation";
import { AccordionSummary, Grid, Typography, Box, Divider, Accordion, AccordionDetails, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import CurrentUserContext from "@/Contexts/CurrentUser";
import CreateClientModal from "@/components/Client/CreateClientModal";
import OverwatchModal from "@/components/OverwatchModal";
import MultipleSettingsForm from "@/components/ProjectSettings";
import { Event, Project, PermissionActionEnum, PermissionEntityEnum, UpdateOnboardingStepDocument, OnboardingStepsEnum } from "@/graphql/generated/graphql";
import { BLUE2 } from "@/theme/palette";
import { MinimizedModalsType } from "@/types/globals";
import { ProjectContext } from "@/Contexts/Project/Context";
import PermissionContext from "@/Contexts/Permissions";
import ProjectFilesDrawer from "../ProjectFilesDrawer";
import CircularLoader from "@/components/Loaders/CircularLoader";
import { useMutation } from "@apollo/client/react";
import { updateOnboardingCache } from "@/utils/helperFunctions";

interface CreateProjectModalProps {
  minimized?: boolean;
  handleClose: () => void;
  handleMinimize?: (label: MinimizedModalsType["label"]) => void;
  parentSource?: Event | Project;
  projectId?: string;
};

const sectionSx = {
  position: "relative",
  px: 2,
  pt: 3,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  height: "100%",
  display: "flex",
  flexDirection: "column",
} as const;

const sectionLabelSx = {
  position: "absolute",
  top: "-15px",
  px: 0.5,
  backgroundColor: "background.paper",
} as const;

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  minimized = false,
  handleClose,
  handleMinimize = () => { },
  parentSource,
}) => {
  const { mode, onSettingsChange, loadingProject, project, setProject, handleCreateProject, handleResetProject, isSaving, lastSaved } = useContext(ProjectContext);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const { canPerformAction } = useContext(PermissionContext);
  const { Create } = PermissionActionEnum;
  const { ProjectKickOff } = PermissionEntityEnum;

  const { currentUser, currentCompany } = useContext(CurrentUserContext);
  const [updateOnboardingStep] = useMutation(UpdateOnboardingStepDocument);

  const getCaption = () => {
    if (isSaving) return mode === "edit" ? "Saving..." : "Auto-saving...";
    if (lastSaved) return mode === "edit" ? "Changes saved" : "Draft auto-saved";

    return `Fill out the details below to ${mode === "edit" ? "edit the" : "create a new"} project`;
  }

  if (project?.clientId === "0")
    return (
      <CreateClientModal
        // Clear the "add new client" sentinel only if no client was created,
        // so cancelling returns to an empty selection
        handleClose={() =>
          setProject((prev) =>
            prev.clientId === "0" ? { ...prev, clientId: "" } : prev
          )
        }
        onSuccess={(client) =>
          setProject((prev) => ({
            ...prev,
            clientId: client?.id ?? "",
          }))
        }
      />
    );

  if (loadingProject) return <CircularLoader />;

  return (
    <OverwatchModal
      size="xl"
      title={`${mode === "edit" ? "Edit" : "New"} Project Form`}
      handleClose={() => { setProject({ ...DEFAULT_PROJECT_STATE }); handleClose(); }}
      handleMinimize={() => handleMinimize(project.title || `${mode === "edit" ? "Edit" : "New"} Project`)}
      primaryButton={<></>}
      secondaryButton={<></>}
      tertiaryButton={<>
        <Button disabled={mode === "edit"} onClick={() => setShowResetConfirm(true)}> Discard </Button>
        <Button onClick={() => {
          handleCreateProject({
            onSuccess: () => {
              setProject({ ...DEFAULT_PROJECT_STATE });
              handleClose();

              // update onboarding cache in case the onboarding was left incomplete
              if (!currentCompany?.onboardingSteps?.["projectCreated"]) {
                updateOnboardingStep({
                  variables: { step: OnboardingStepsEnum.ProjectCreated },
                  update(cache, { data }) {
                    updateOnboardingCache(cache, data?.updateOnboardingStep);
                  },
                });
              }
            }
          });
        }}
          disabled={!canCreateProject(project)}> Save </Button>
      </>
      }
      // tertiaryButtonText=""
      // tertiaryButtonAction={}
      subtitle={parentSource ? formatSourceText(parentSource) : ""}
      caption={getCaption()}
      minimized={minimized}
      fullScreen={true}
      paperSx={{ zIndex: 999999 }}
    >
      {
        loadingProject ? <CircularLoader /> :
          <Fragment>
            <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={sectionSx}>
                  <Typography sx={sectionLabelSx} variant="overline"> Project Details </Typography>

                  <ProjectBasics />
                  <ClientBasics />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ ...sectionSx, p: 2, pt: 1 }}>
                  <Typography sx={sectionLabelSx} variant="overline"> Managers List </Typography>

                  <ProjectManagers />
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ mt: 8, mb: 2 }}>
              <Typography variant="overline"> Site Addresses (Optional) </Typography>
            </Divider>

            <MultipleAddressesForm />

            <Divider sx={{ my: 2 }}>
              <Typography variant="overline"> Project Configuration </Typography>
            </Divider>

            {
              (currentUser?.isOwner || canPerformAction(Create, ProjectKickOff)) &&
              <Accordion>
                <AccordionSummary sx={{ backgroundColor: BLUE2 }}>
                  <Typography> Project Activities </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: "2px", lineHeight: "24px" }}> (Optional)</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', ml: "auto" }}>
                    Set activities for the project
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <MultipleActivitiesForm />
                </AccordionDetails>
              </Accordion>
            }

            <Grid container spacing={1} sx={{ marginTop: 1, marginBottom: 1, alignItems: { md: "flex-start" } }}>
              <Grid size={{ xs: 12, md: 3 }} sx={{ minWidth: 0 }}>
                <Accordion disableGutters id='project-contract-types' sx={{ flex: 1, width: { xs: "100%", md: "auto" } }}>
                  <AccordionSummary sx={{ backgroundColor: BLUE2 }}>
                    <Typography> Contract Types </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: "2px", lineHeight: "24px" }}> (Optional)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <ContractTypes />
                  </AccordionDetails>
                </Accordion>
              </Grid>

              <Grid size={{ xs: 12, md: 3 }} sx={{ minWidth: 0 }}>
                <Accordion disableGutters id="project-tags" sx={{ flex: 1, width: { xs: "100%", md: "auto" } }}>
                  <AccordionSummary sx={{ backgroundColor: BLUE2 }}>
                    <Typography> Project Tags</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: "2px", lineHeight: "24px" }}> (Optional)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <ProjectTags />
                  </AccordionDetails>
                </Accordion>
              </Grid>

              <Grid size={{ xs: 12, md: 3 }} sx={{ minWidth: 0 }}>
                <Accordion disableGutters sx={{ flex: 1, width: { xs: "100%", md: "auto" } }}>
                  <AccordionSummary sx={{ backgroundColor: BLUE2 }}>
                    <Typography> Project Settings </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: "2px", lineHeight: "24px" }}> (Optional)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MultipleSettingsForm
                      showHeading={false}
                      settings={project.settings || []}
                      onSettingsChange={onSettingsChange}
                    />
                  </AccordionDetails>
                </Accordion>
              </Grid>

              <Grid size={{ xs: 12, md: 3 }} sx={{ minWidth: 0 }}>
                <Accordion disableGutters sx={{ flex: 1, width: { xs: "100%", md: "auto" } }}>
                  <AccordionSummary sx={{ backgroundColor: BLUE2 }}>
                    <Typography> Documents </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: "2px", lineHeight: "24px" }}> (Optional)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <ProjectFilesDrawer projectId={project.id as string} />
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
          </Fragment>
      }

      <Dialog open={showResetConfirm} onClose={() => setShowResetConfirm(false)}>
        <DialogTitle>Discard Project</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This is an irreversible action. All entered data{project.id ? " and the auto-saved draft" : ""} will be permanently deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResetConfirm(false)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              setShowResetConfirm(false);
              handleResetProject();
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

    </OverwatchModal>
  );
};

export default CreateProjectModal;
